import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, comics } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory, slug]);

  const filteredComics = useMemo(() => {
    let result = activeCategory === 'all'
      ? [...comics]
      : comics.filter((c) => c.category === activeCategory);

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
    }
    return result;
  }, [activeCategory, sortBy]);

  const currentCategoryName = categories.find((c) => c.slug === activeCategory)?.name || '全部';

  return (
    <main className="min-h-screen pt-16">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb items={[
          { label: '首页', to: '/' },
          { label: currentCategoryName },
        ]} />

        <div className="flex flex-col lg:flex-row gap-6 mt-4">
          <aside className="lg:w-48 flex-shrink-0">
            <nav className="sticky top-20 bg-background-100 rounded-xl p-3">
              <h3 className="text-sm font-semibold text-foreground-950 mb-3 px-2">漫画分类</h3>
              <div className="flex lg:flex-col flex-wrap gap-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </nav>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
              <div>
                <h1 className="text-xl md:text-2xl font-heading text-foreground-950">
                  {currentCategoryName}漫画
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {filteredComics.length} 部作品
                </p>
              </div>

              <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
                {[
                  { key: 'popular', label: '最热' },
                  { key: 'newest', label: '最新' },
                  { key: 'rating', label: '评分' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key as typeof sortBy)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === s.key
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredComics.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
                <p className="text-foreground-500 mt-4">暂无相关漫画</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${currentCategoryName}漫画 - 香蕉文化漫画网`,
            "url": `https://www.xiangjiaowenhua.com/category/${activeCategory}`,
            "description": `香蕉文化漫画网${currentCategoryName}专区，海量精品${currentCategoryName}资源，流畅在线阅读体验`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "香蕉文化漫画网"
            }
          })
        }}
      />
    </main>
  );
}